import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import sessionsAPI from '../api/sessions';
import messagesAPI from '../api/messages';
import threadsAPI from '../api/threads';
import usersAPI from '../api/users';
import ChatWebSocket from '../services/chatWebSocket';
import LoadingDots from './LoadingDots';
import transcriptionAPI from '../api/transcription';
import './ChatSession.css';

const ChatSession = () => {
  const { hash_id, thread_id } = useParams();
  const navigate = useNavigate();
  const { user, userId } = useAuth();
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isShareLink, setIsShareLink] = useState(false);
  const [threadOwner, setThreadOwner] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [threadData, setThreadData] = useState(null);
  
  // Message and chat state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartMs, setRecordingStartMs] = useState(null);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const [recordings, setRecordings] = useState([]); // { id, url, blob, durationMs }
  const [isTranscribing, setIsTranscribing] = useState(false);
  
  // Long-press/modal state
  const [isInstructionModalOpen, setIsInstructionModalOpen] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  
  // Label editing state
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editingLabel, setEditingLabel] = useState('');
  const [isUpdatingLabel, setIsUpdatingLabel] = useState(false);
  
  // Refs
  const textareaRef = useRef(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const timerRef = useRef(null);

  // Message sending logic
  const handleSendMessage = async () => {
    // If we have recordings, transcribe them first
    if (recordings.length > 0) {
      await handleTranscribeAndSend();
      return;
    }

    if (!inputText.trim()) return;
    
    if (!wsRef.current) return;

    const messageText = inputText.trim();
    setInputText('');

    try {
      // Create user message immediately
      const userMessage = {
        id: Date.now(), // Temporary ID
        role: 'user',
        content: messageText,
        created_at: new Date().toISOString(),
        session_id: session?.id,
        streams: []
      };

      setMessages(prev => [...prev, userMessage]);

      // Send message via WebSocket
      wsRef.current.sendMessage(messageText);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleTranscribeAndSend = async () => {
    if (recordings.length === 0) return;
    
    setIsTranscribing(true);
    
    try {
      if (!wsRef.current) return;

      // Transcribe all recordings
      const transcriptions = [];
      for (const recording of recordings) {
        try {
          const transcription = await transcriptionAPI.transcribeAudio(recording.blob);
          if (transcription) {
            transcriptions.push(transcription);
          }
        } catch (error) {
          console.error('Failed to transcribe recording:', error);
        }
      }

      if (transcriptions.length === 0) {
        alert('Failed to transcribe any recordings. Please try again.');
        return;
      }

      // Combine all transcriptions
      const combinedText = transcriptions.join(' ');
      setInputText(combinedText);

      // Create user message immediately
      const userMessage = {
        id: Date.now(), // Temporary ID
        role: 'user',
        content: combinedText,
        created_at: new Date().toISOString(),
        session_id: session?.id,
        streams: []
      };

      setMessages(prev => [...prev, userMessage]);

      // Send message via WebSocket
      wsRef.current.sendMessage(combinedText);

      // Clear recordings and input text after successful send
      setRecordings([]);
      setInputText('');
    } catch (error) {
      console.error('Failed to transcribe and send:', error);
      alert('Failed to transcribe recordings. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  // Recording helpers
  const formatElapsed = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const startTimer = (startMs) => {
    setRecordingStartMs(startMs);
    setRecordingElapsedMs(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRecordingElapsedMs(Date.now() - startMs);
    }, 200);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Long-press detection for mic button
  const LONG_PRESS_MS = 600; // threshold for considering a long press

  const handleMicPointerDown = (e) => {
    // Prevent default to avoid context menu/long-press actions on mobile
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    longPressTriggeredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setIsInstructionModalOpen(true);
    }, LONG_PRESS_MS);
  };

  const handleMicPointerUp = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    // If long-press did not trigger, treat as a tap/click
    if (!longPressTriggeredRef.current) {
      startRecording();
    }
  };

  const handleMicPointerLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const startRecording = async () => {
    if (isRecording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      // Try to use a more mobile-friendly format
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/mpeg')) {
        mimeType = 'audio/mpeg';
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const durationMs = Date.now() - (recordingStartMs || Date.now());
        setRecordings((prev) => [
          { id: Date.now(), url, blob, durationMs, mimeType },
          ...prev,
        ]);
        stopTimer();
        setRecordingStartMs(null);
        setRecordingElapsedMs(0);
      };
      mediaRecorder.start();
      setIsRecording(true);
      startTimer(Date.now());
    } catch (err) {
      console.error('Failed to start recording:', err);
      setIsRecording(false);
      stopTimer();
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch (e) {
      console.error('Failed to stop MediaRecorder:', e);
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    setIsRecording(false);
  };

  const deleteRecording = (id) => {
    setRecordings((prev) => {
      const rec = prev.find((r) => r.id === id);
      if (rec && rec.url) URL.revokeObjectURL(rec.url);
      return prev.filter((r) => r.id !== id);
    });
  };

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Label editing functions
  const handleEditLabel = () => {
    setEditingLabel(session?.label || '');
    setIsEditingLabel(true);
  };

  const handleCancelEdit = () => {
    setIsEditingLabel(false);
    setEditingLabel('');
  };

  const handleSaveLabel = async () => {
    if (!session?.id || !editingLabel.trim()) return;
    
    setIsUpdatingLabel(true);
    try {
      await sessionsAPI.updateSessionLabel(session.id, editingLabel.trim());
      setSession(prev => ({ ...prev, label: editingLabel.trim() }));
      setIsEditingLabel(false);
      setEditingLabel('');
    } catch (error) {
      console.error('Failed to update session label:', error);
      alert('Failed to update session label. Please try again.');
    } finally {
      setIsUpdatingLabel(false);
    }
  };

  const handleLabelKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveLabel();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // WebSocket event handlers
  const handleMessageStart = (data) => {
    console.log('Message start:', data);
    setIsLoadingResponse(true);
    
    // Create a new assistant message with loading state
    const assistantMessage = {
      id: data.messageId,
      role: 'assistant',
      content: '',
      created_at: new Date().toISOString(),
      session_id: session?.id,
      streams: [],
      isLoading: true
    };
    
    setStreamingMessage(assistantMessage);
    setMessages(prev => [...prev, assistantMessage]);
  };

  const handleMessageEnd = (data) => {
    console.log('Message end:', data);
    setIsLoadingResponse(false);
    setStreamingMessage(null);
    
    // Update the message to remove loading state
    setMessages(prev => prev.map(msg => 
      msg.id === data.messageId 
        ? { ...msg, isLoading: false }
        : msg
    ));
  };

  const handleStreamCreate = (data) => {
    console.log('Stream create:', data);
    
    const newStream = {
      stream_id: data.streamId,
      content: '',
      created_at: data.createdAt
    };
    
    // Update the message in the messages array
    setMessages(prev => prev.map(msg => 
      msg.id === data.messageId 
        ? { ...msg, streams: [...(msg.streams || []), newStream] }
        : msg
    ));
    
    // Also update the streaming message if it matches
    if (streamingMessage && streamingMessage.id === data.messageId) {
      setStreamingMessage(prev => ({
        ...prev,
        streams: [...(prev.streams || []), newStream]
      }));
    }
  };

  const handleStreamDelta = (data) => {
    console.log('🟢 STREAM_DELTA handler called:', data);
    
    // Update the message in the messages array
    setMessages(prev => {
      const updatedMessages = prev.map(msg => {
        if (msg.id === data.messageId) {
          const updatedStreams = (msg.streams || []).map(stream => {
            if (stream.stream_id === data.streamId) {
              const newContent = stream.content + (data.delta || '');
              console.log(`🟢 Updating stream ${data.streamId}: "${stream.content}" + "${data.delta}" = "${newContent}"`);
              return { ...stream, content: newContent };
            }
            return stream;
          });
          return { ...msg, streams: updatedStreams };
        }
        return msg;
      });
      return updatedMessages;
    });
    
    // Also update the streaming message if it matches
    if (streamingMessage && streamingMessage.id === data.messageId) {
      setStreamingMessage(prev => ({
        ...prev,
        streams: (prev.streams || []).map(stream => 
          stream.stream_id === data.streamId
            ? { ...stream, content: stream.content + (data.delta || '') }
            : stream
        )
      }));
    }
  };

  // Initialize WebSocket connection
  const initializeWebSocket = async (sessionHashId, threadId) => {
    if (wsRef.current) {
      wsRef.current.destroy();
    }

    wsRef.current = new ChatWebSocket();
    
    // Set up event handlers
    console.log('🔧 Setting up WebSocket event handlers');
    wsRef.current.on('message_start', handleMessageStart);
    wsRef.current.on('message_end', handleMessageEnd);
    wsRef.current.on('stream_create', handleStreamCreate);
    wsRef.current.on('stream_delta', handleStreamDelta);
    wsRef.current.on('error', (error) => {
      console.error('WebSocket error:', error);
    });
    console.log('🔧 WebSocket event handlers registered');

    try {
      await wsRef.current.connect(sessionHashId, threadId);
      console.log('WebSocket connected for session:', sessionHashId, 'thread:', threadId);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  // Helper function to get streams content
  const getStreamsContent = (streams) => {
    if (!streams || streams.length === 0) return '';
    return streams.map(stream => stream.content || '').join('');
  };

  // Fetch thread owner information
  const fetchThreadOwner = async (threadId) => {
    try {
      // Use the basic user API that gets thread owner info directly
      const ownerData = await usersAPI.getBasicUserByThreadId(threadId);
      setThreadOwner(ownerData);
    } catch (error) {
      console.error('Failed to fetch thread owner:', error);
    }
  };

  // Fetch current user data
  const fetchCurrentUser = async () => {
    if (!userId) return;
    try {
      const userData = await usersAPI.getBasicUserById(userId);
      setCurrentUser(userData);
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    }
  };

  // Fetch thread data for shared sessions
  const fetchThreadData = async (threadId) => {
    try {
      const thread = await threadsAPI.getThreadByIdPublic(threadId);
      setThreadData(thread);
      return thread;
    } catch (error) {
      console.error('Failed to fetch thread data:', error);
      return null;
    }
  };

  // Create first message from thread data
  const createFirstMessage = (thread) => {
    if (!thread?.first_message) return null;
    
    // Create a stream for the first message content
    const firstStream = {
      stream_id: `first-stream-${thread.id}`,
      content: thread.first_message,
      created_at: thread.created_at || new Date().toISOString()
    };
    
    return {
      id: `first-message-${thread.id}`,
      role: 'assistant',
      content: '', // Leave content empty, use streams instead
      created_at: thread.created_at || new Date().toISOString(),
      session_id: session?.id,
      streams: [firstStream],
      isFirstMessage: true
    };
  };

  useEffect(() => {
    fetchCurrentUser();
  }, [userId]);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        setIsLoading(true);
        
        // Check if this is a share link case (hash_id looks like a generated random string)
        const isGeneratedHash = hash_id && hash_id.length > 10 && /^[a-z0-9]+$/.test(hash_id);
        
        if (isGeneratedHash) {
          // This is a share link case, try to fetch session first
          setIsShareLink(true);
          
          try {
            // Try to fetch the actual session data first
            const sessionData = await sessionsAPI.getSessionByHashId(hash_id);
            if (sessionData) {
              setSession(sessionData);
            } else {
              // If no session exists, create a mock session
              const mockSession = {
                id: null, // Will be set when a real session is created
                hash_id: hash_id,
                conversation_thread_id: parseInt(thread_id),
                label: 'Shared Chat Session',
                created_at: new Date().toISOString(),
                user_ids: [],
                users: []
              };
              setSession(mockSession);
            }
          } catch (sessionErr) {
            // If session fetch fails, create a mock session
            console.log('No existing session found, creating mock session for share link');
            const mockSession = {
              id: null, // Will be set when a real session is created
              hash_id: hash_id,
              conversation_thread_id: parseInt(thread_id),
              label: 'Shared Chat Session',
              created_at: new Date().toISOString(),
              user_ids: [],
              users: []
            };
            setSession(mockSession);
          }
          
          // Fetch thread owner information and thread data for share links
          if (thread_id) {
            await fetchThreadOwner(parseInt(thread_id));
            await fetchThreadData(parseInt(thread_id));
          }
        } else {
          // Normal case, fetch session from API
          const sessionData = await sessionsAPI.getSessionByHashId(hash_id);
          setSession(sessionData);
          
          // Fetch thread owner information
          if (sessionData && sessionData.conversation_thread_id) {
            await fetchThreadOwner(sessionData.conversation_thread_id);
            
            // Only check for redirect if user is logged in
            if (userId) {
              try {
                const threadData = await threadsAPI.getThreadById(sessionData.conversation_thread_id);
                if (threadData && threadData.owner_id === userId) {
                  // User is the owner, redirect to MyChatSession
                  navigate(`/my/chat/session/${hash_id}/thread/${thread_id}`, { replace: true });
                  return;
                }
              } catch (threadErr) {
                console.error('Failed to fetch thread:', threadErr);
                // Continue with normal ChatSession if thread fetch fails
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch session:', err);
        setError('Failed to load session');
      } finally {
        setIsLoading(false);
      }
    };

    if (hash_id) {
      fetchSession();
    }
  }, [hash_id, thread_id, userId, navigate]);

  // Load messages when session is loaded
  useEffect(() => {
    const loadMessages = async () => {
      if (!session?.id) return; // Only skip if no session ID
      
      setIsLoadingMessages(true);
      try {
        const sessionMessages = await messagesAPI.getMessagesBySession(session.id, 100, 0);
        if (Array.isArray(sessionMessages)) {
          setMessages(sessionMessages);
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error('Failed to load session messages:', error);
        setMessages([]);
      } finally {
        setIsLoadingMessages(false);
      }
    };

    if (session?.id) {
      // Load messages for any session with a valid ID (including share links with real sessions)
      loadMessages();
    } else if (isShareLink) {
      // For share link cases without session ID, start with empty messages
      setMessages([]);
      setIsLoadingMessages(false);
    }
  }, [session?.id, isShareLink]);

  // Add first message for shared sessions with no messages
  useEffect(() => {
    if (isShareLink && threadData && messages.length === 0) {
      const firstMessage = createFirstMessage(threadData);
      if (firstMessage) {
        setMessages([firstMessage]);
      }
    }
  }, [isShareLink, threadData, messages.length]);

  // Initialize WebSocket when session is loaded
  useEffect(() => {
    if (session?.hash_id && thread_id) {
      initializeWebSocket(session.hash_id, thread_id);
    }

    // Cleanup WebSocket on unmount or session change
    return () => {
      if (wsRef.current) {
        wsRef.current.destroy();
        wsRef.current = null;
      }
    };
  }, [session?.hash_id, thread_id]);

  // Auto-resize textarea - removed to prevent unwanted expansion

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  // Cleanup WebSocket and recording resources on component unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.destroy();
      }
      // Cleanup recording resources
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordings((prev) => {
        prev.forEach((r) => r.url && URL.revokeObjectURL(r.url));
        return [];
      });
    };
  }, []);

  if (isLoading) {
    return (
      <div className="chat-session">
        <div className="chat-session-loading">
          <div className="loading-spinner"></div>
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-session">
        <div className="chat-session-error">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="chat-session">
        <div className="chat-session-error">
          <h3>Session Not Found</h3>
          <p>The requested session could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-session">
      <div className="chat-session-header">
        <div className="chat-session-logo" onClick={() => navigate('/dashboard')}>
          <div className="logo-placeholder">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </div>
        </div>
        <div className="chat-session-title">
          <div className="session-title-container">
            {isEditingLabel ? (
              <div className="session-title-edit">
                <input
                  type="text"
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onKeyDown={handleLabelKeyPress}
                  className="session-title-input"
                  placeholder="Enter session name..."
                  autoFocus
                  disabled={isUpdatingLabel}
                />
                <div className="session-title-actions">
                  <button
                    className="session-title-save"
                    onClick={handleSaveLabel}
                    disabled={isUpdatingLabel || !editingLabel.trim()}
                    title="Save"
                  >
                    {isUpdatingLabel ? (
                      <LoadingDots />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    )}
                  </button>
                  <button
                    className="session-title-cancel"
                    onClick={handleCancelEdit}
                    disabled={isUpdatingLabel}
                    title="Cancel"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="session-title-display">
                <h1>{session.label || 'Untitled Session'}</h1>
                {session?.id && (
                  <button
                    className="session-title-edit-btn"
                    onClick={handleEditLabel}
                    title="Edit session name"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
          {threadOwner && (
            <div className="thread-owner-info">
              <div className="owner-avatar">
                {threadOwner.profile_picture_url ? (
                  <img 
                    src={threadOwner.profile_picture_url} 
                    alt="Profile" 
                    className="avatar-image"
                  />
                ) : (
                  <div className="avatar-initial">
                    {threadOwner.username ? threadOwner.username.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
              </div>
              <span className="owner-username">{threadOwner.username}</span>
            </div>
          )}
        </div>
        {userId && (
          <div className="chat-session-profile" onClick={() => navigate('/dashboard')}>
            {currentUser?.profile_picture_url ? (
              <img 
                src={currentUser.profile_picture_url} 
                alt="Profile" 
                className="profile-picture-image"
              />
            ) : (
              <div className="profile-picture">
                {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="chat-session-content">
        <div className="chat-container">
          <div className="messages-container">
            {isLoadingMessages ? (
              <div className="loading-messages">
                <LoadingDots />
                <p>Loading messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="empty-state">
                <div className="no-session-state">
                  <div className="no-session-content">
                    <h3>Welcome to your chat session! 👋</h3>
                    <p>Start a conversation by typing a message or recording your voice.</p>
                    <div className="voice-hint" style={{ marginTop: '12px' }}>
                      <div className="voice-hint-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                        </svg>
                      </div>
                      <div className="voice-hint-text">
                        You can chat with voice. Tap the microphone button to start recording. Record your message and send it.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              messages.map((message, index) => {
                const isLastMessage = index === messages.length - 1;
                return (
                  <div 
                    key={`${message.id}-${message.streams?.length || 0}-${message.streams?.map(s => s.content?.length || 0).join('-') || 0}`} 
                    className={`message-container ${message.role === 'user' ? 'user-message-container' : 'assistant-message-container'} ${isLastMessage ? 'last-message' : ''}`}
                    style={isLastMessage ? { 
                      minHeight: window.innerWidth <= 768 ? '15vh' : '25vh',
                      marginBottom: '30vh'
                    } : {}}
                  >
                    <div className="message-content-wrapper">
                      {message.role === 'user' ? (
                        <>
                          <div className="message-avatar user-avatar">
                            {currentUser?.profile_picture_url ? (
                              <img 
                                src={currentUser.profile_picture_url} 
                                alt="Profile" 
                                className="avatar-image"
                              />
                            ) : (
                              <div className="avatar-initial">
                                {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                              </div>
                            )}
                          </div>
                          <div className="user-message-bubble">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="message-avatar assistant-avatar">
                            {threadOwner?.profile_picture_url ? (
                              <img 
                                src={threadOwner.profile_picture_url} 
                                alt="Profile" 
                                className="avatar-image"
                              />
                            ) : (
                              <div className="avatar-initial">
                                {threadOwner?.username ? threadOwner.username.charAt(0).toUpperCase() : 'AI'}
                              </div>
                            )}
                          </div>
                          <div className="assistant-message-content">
                            <div className="assistant-message-header">
                              <span className="assistant-username">
                                {threadOwner?.username || 'AI Assistant'}
                              </span>
                            </div>
                            <div className="assistant-message-text">
                              {(() => {
                                const streamContent = getStreamsContent(message.streams);
                                const hasStreams = message.streams && message.streams.length > 0;
                                const hasStreamContent = streamContent && streamContent.trim().length > 0;
                                const hasMessageContent = message.content && message.content.trim().length > 0;
                                
                                // Prioritize stream content over message content
                                if (hasStreams && hasStreamContent) {
                                  return <ReactMarkdown>{streamContent}</ReactMarkdown>;
                                } else if (hasMessageContent) {
                                  return <ReactMarkdown>{message.content}</ReactMarkdown>;
                                } else if (message.isLoading) {
                                  return <LoadingDots />;
                                } else {
                                  return <ReactMarkdown>{streamContent || message.content || ''}</ReactMarkdown>;
                                }
                              })()}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        <div className="input-container">
          <div className={`input-wrapper ${(isRecording || recordings.length > 0) ? 'input-wrapper-no-text' : ''}`}>
            {!userId ? (
              <div className="login-input-area">
                <div className="login-input-text">
                  Login/signup to start chatting
                </div>
                <button 
                  className="login-input-button"
                  onClick={() => navigate(`/dashboard?redirect=${encodeURIComponent(window.location.pathname)}`)}
                >
                  Sign In / Sign Up
                </button>
              </div>
            ) : (
              <>
                {!isRecording && recordings.length === 0 && (
                  <textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="message-input"
                    rows={1}
                  />
                )}
                {recordings.length > 0 && (
                  <div className="recordings-list">
                    {recordings.map((rec) => (
                      <div key={rec.id} className="recording-pill">
                        <div className="recording-icon" />
                        <audio 
                          src={rec.url} 
                          controls 
                          preload="metadata"
                          playsInline
                          webkit-playsinline="true"
                          style={{ maxWidth: '120px', height: '32px' }}
                          onError={(e) => {
                            console.error('Audio playback error:', e);
                            console.log('Audio format:', rec.mimeType || 'unknown');
                          }}
                        />
                        <span className="recording-duration">{formatElapsed(rec.durationMs)}</span>
                        <button className="recording-delete-button" title="Delete recording" onClick={() => deleteRecording(rec.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="input-buttons">
                  {!isRecording ? (
                    <button
                      className="mic-button"
                      title="Start recording"
                      onPointerDown={handleMicPointerDown}
                      onPointerUp={handleMicPointerUp}
                      onPointerLeave={handleMicPointerLeave}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                      </svg>
                    </button>
                  ) : (
                    <div className="recording-controls">
                      <div className="recording-timer">
                        <span className="recording-dot" /> {formatElapsed(recordingElapsedMs)}
                      </div>
                      <button className="stop-recording-button" title="Stop recording" onClick={stopRecording}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="6" width="12" height="12" rx="2" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <button 
                    onClick={handleSendMessage}
                    className="send-button"
                    disabled={(!inputText.trim() && recordings.length === 0) || isTranscribing}
                    title={
                      isTranscribing 
                        ? "Transcribing..." 
                        : recordings.length > 0 
                          ? "Transcribe and send recordings" 
                          : "Send message"
                    }
                  >
                    {isTranscribing ? (
                      <LoadingDots />
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        
        {isInstructionModalOpen && (
          <div className="modal-overlay" onClick={() => setIsInstructionModalOpen(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-title">How to use voice recording</div>
              <div className="modal-body">
                Tap the microphone once to start recording, then press stop when you're done. When you're ready, send the recording.
              </div>
              <div className="modal-actions">
                <button className="modal-button" onClick={() => setIsInstructionModalOpen(false)}>OK</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSession;
